import { GetStaticPaths, GetStaticProps } from "next";
import { useRouter } from "next/router";

import { FEATURE_CONTENT_MAP, type FeatureContent } from "@/lib/features/content";
import FeaturePageBody from "@/components/pages/FeaturePageBody";
import NotFoundView from "@/components/pages/NotFoundView";

const STATIC_FEATURE_PAGES = ["api"];

interface FeaturePageProps {
  featureContent: FeatureContent | null;
}

const FeaturePage = ({ featureContent: initialFeatureContent }: FeaturePageProps) => {
  const router = useRouter();
  const feature = typeof router.query.feature === "string" ? router.query.feature : "";
  const featureContent =
    initialFeatureContent ?? (feature ? (FEATURE_CONTENT_MAP[feature] ?? null) : null);

  if (!featureContent) {
    if (!router.isReady) return null;

    return <NotFoundView message="The feature you're looking for doesn't exist." />;
  }

  return <FeaturePageBody content={featureContent} locale="en" path={`/features/${feature}`} />;
};

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = Object.keys(FEATURE_CONTENT_MAP)
    .filter((feature) => !STATIC_FEATURE_PAGES.includes(feature))
    .map((feature) => ({
      params: { feature },
    }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<FeaturePageProps> = async ({ params }) => {
  const feature = params?.feature as string;
  const featureContent = FEATURE_CONTENT_MAP[feature] || null;

  return {
    props: {
      featureContent,
    },
  };
};

export default FeaturePage;
